import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Image, Link, Text } from '@chakra-ui/react';
import { useMfaSetupMutation } from 'setup/authOverrides';
import { MfaSetupResponse } from 'setup/mfaTypes';
import MfaVerify from '../MfaVerify';

type Props = {
  mfaToken: string;
  isVerifying: boolean;
  verifyError?: string;
  onVerify: (code: string, setupKey?: string) => void;
  onBack: () => void;
};

const MfaSetup: React.FC<Props> = ({
  mfaToken,
  isVerifying,
  verifyError,
  onVerify,
  onBack,
}) => {
  const [mfaSetup, { isLoading: isLoadingSetup }] = useMfaSetupMutation();
  const [setupKey, setSetupKey] = useState<string>();
  const [manualEntryKey, setManualEntryKey] = useState<string>();
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState<string>();
  const [setupError, setSetupError] = useState<string>();

  useEffect(() => {
    mfaSetup({ mfaToken })
      .unwrap()
      .then((response: MfaSetupResponse) => {
        setSetupKey(response.setupKey);
        setManualEntryKey(response.manualEntryKey);
        setQrCodeImageUrl(response.qrCodeImageUrl);
      })
      .catch(() => {
        setSetupError('Não foi possível carregar a configuração do MFA.');
      });
  }, [mfaSetup, mfaToken]);

  const handleVerify = useCallback(
    (code: string) => {
      if (!setupKey) return;
      onVerify(code, setupKey);
    },
    [onVerify, setupKey],
  );

  if (isLoadingSetup || !setupKey) {
    return (
      <Box>
        <Text mb="18px" fontWeight="500" fontSize="22px" color="black">
          Configurando autenticação em dois fatores
        </Text>
        <Text fontSize="sm" color="text.300">
          {setupError || 'Carregando QR Code...'}
        </Text>
        {setupError && (
          <Flex mt="18px" justifyContent="center">
            <Link as="button" type="button" fontSize="sm" color="ocean.200" onClick={onBack}>
              Voltar ao login
            </Link>
          </Flex>
        )}
      </Box>
    );
  }

  return (
    <Box data-cy="mfa-setup">
      <Text mb="18px" fontWeight="500" fontSize="22px" color="black">
        Configure o autenticador
      </Text>
      <Text fontSize="sm" color="text.300" mb="18px">
        Escaneie o QR Code com Google Authenticator ou Microsoft Authenticator e
        informe o código gerado.
      </Text>

      {qrCodeImageUrl && (
        <Flex justifyContent="center" mb="18px">
          <Image alt="QR Code MFA" src={qrCodeImageUrl} maxW="220px" />
        </Flex>
      )}

      {manualEntryKey && (
        <Text
          fontSize="sm"
          color="text.300"
          mb="18px"
          wordBreak="break-all"
          data-cy="mfa-manual-key"
        >
          Chave manual: <strong>{manualEntryKey}</strong>
        </Text>
      )}

      <MfaVerify
        isLoading={isVerifying}
        errorMessage={verifyError}
        onSubmit={handleVerify}
        onBack={onBack}
      />
    </Box>
  );
};

export default MfaSetup;
